/**
 * ExteletRegistrationWizard — EXTELET-V3 · Phase 6 + 6.1
 *
 * One React component, two entry points (same flow):
 *   • Discovery → Promote (passes seedFromDiscovery — pre-fills + auto-recommends)
 *   • /agents → "+ New ExteLet" (no seed — opens blank with sensible defaults)
 *
 * Six guided steps:
 *   1. Bind Agent Framework (Platform Connector)
 *   2. Connect the ExteLet (identity + invocation pattern)
 *   3. Choose integration path (CODED Spring AOP @Governed vs Off-the-shelf MCP)
 *   4. Mint credentials (atomic: hits POST /api/v1/gel/registrations/onboard;
 *      plaintext Gateway + SDK or MCP keys shown ONCE)
 *   5. Wire it up (Python / Java / MCP snippet tabs — copy-paste ready)
 *   6. Activate (PENDING_VERIFICATION on file; auto-flips to VERIFIED on
 *      the first successful governed call — no manual Verify step)
 *
 * Failure semantics — the API call in step 4 runs the entire onboard()
 * inside ONE DB transaction; ANY failure rolls back the registration,
 * the Agent row, and every key minted. Wizard surfaces the error and
 * lets the operator retry from step 4. No half-onboarded ExteLets.
 */
import React, { useEffect, useMemo, useState } from 'react';
/* The per-step icons moved to data/registerSteps.js with the steps themselves;
   what is left here is what this file draws directly. */
import {
  Plug, Code2, AlertTriangle,
  ArrowRight, ArrowLeft, Sparkles, Copy, Check, ShieldCheck, Info,
} from 'lucide-react';
import { Slideout, Button, Pill, Card } from './index';
import * as api from '../../services/api';
import { useToast } from '../../contexts/ToastContext';
import { useWizardDraft } from '../../hooks/useWizardDraft';
/* P7J-RUN-033 — outbound links to /gel-connectors used <a target="_blank"
 * rel="noreferrer">. The session cookie is SameSite=Strict (per api.js
 * G409 comment) so a new-tab top-level navigation does NOT carry the
 * cookie → backend treats the new tab as unauthenticated → ProtectedRoute
 * redirects to /login. Use react-router's Link component for SPA-internal
 * navigation; same-tab, no cookie loss, no page reload. */
import { Link as RouterLink } from 'react-router-dom';

/* The seven Register steps — order, labels and icons — now come from
 * data/registerSteps.js, because Protect's build trail walks the same stage and
 * two private lists would let the two screens disagree about what Register is. */

/* The canonical Agent Framework taxonomy — same list that powers the
 * "Add External Agent Framework" wizard on /gel-connectors. Duplicated
 * here (instead of imported) to keep this wizard self-contained and avoid
 * a cross-page import cycle; the source of truth is FRAMEWORK_OPTIONS in
 * GelPlatformConnectorsPageV2.jsx — keep in sync when frameworks are
 * added there.
 *
 * Listing the type taxonomy FIRST (as primary categorization) is the
 * cognitive-model fix the operator asked for: when binding an ExteLet,
 * you pick the FRAMEWORK ("Anthropic"), not a specific connector
 * instance. The secondary connector picker only surfaces if the type
 * has multiple configured connectors. */
import AccountabilitySection, { DEFAULT_ACCOUNTABILITY } from './AccountabilitySection';
import { EntityPicker } from './EntityPicker';
import { FRAMEWORK_CHOICES, FRAMEWORK_TABS, choiceById, firstChoiceForType, typeLabel, isCodedFramework } from './frameworkCatalog';
import { REGISTER_STEPS as STEPS, REGISTER_COMMIT_STEP } from '../../data/registerSteps';

/* Framework catalog is shared — see components/v2/frameworkCatalog.js */

/* DEF-EXTELET-V3-CROSSSTORE-001 — User-reported regression: "Anthropic IS
 * connected in Build > Connectors but the ExteLet wizard says it isn't."
 *
 * Backend has two SEPARATE connector tables:
 *   /api/v1/connectors      — generic connector store (tool integrations)
 *   /api/v1/gel/connectors  — GEL platform connector store (agent-framework
 *                             runtime bindings for ExteLets)
 *
 * Until the BE unifies these (out of FE scope), the wizard cross-probes
 * the generic store so it can tell the operator something honest:
 *   "You DO have an Anthropic connector in Build > Connectors, but it
 *    only configures tool calls — ExteLet runtime bindings need a separate
 *    Platform Connector. Configure one on External Agent Framework page."
 *
 * Map: generic-connector `type` field (e.g. 'anthropic-llm-v1') → framework
 * key (e.g. 'ANTHROPIC'). Type prefix is the canonical match; fall through
 * to a name substring check for connectors with unusual type strings. */
function mapGenericConnectorToFramework(c) {
  if (!c) return null;
  const t = String(c.type || '').toLowerCase();
  const n = String(c.name || '').toLowerCase();
  if (t.startsWith('anthropic'))                          return 'ANTHROPIC';
  if (t.startsWith('azure-openai') || t.startsWith('azureopenai')) return 'AZURE_OPENAI';
  if (t.startsWith('openai'))                             return 'OPENAI';
  if (t.startsWith('vertex'))                             return 'VERTEX_AI';
  if (t.startsWith('agentforce') || t.startsWith('salesforce-agentforce')) return 'AGENTFORCE';
  if (t.startsWith('servicenow'))                         return 'SERVICENOW';
  if (t.startsWith('uipath'))                             return 'UIPATH';
  if (t.startsWith('langchain'))                          return 'LANGCHAIN';
  if (t.startsWith('crewai'))                             return 'CREWAI';
  if (t.startsWith('autogen'))                            return 'AUTOGEN';
  // Name-substring fallbacks for connectors with non-canonical type strings.
  if (n.includes('azure') && n.includes('openai'))        return 'AZURE_OPENAI';
  if (n.includes('anthropic') || n.includes('claude'))    return 'ANTHROPIC';
  if (n.includes('openai') || n.includes('gpt'))          return 'OPENAI';
  if (n.includes('vertex') || n.includes('gemini'))       return 'VERTEX_AI';
  if (n.includes('agentforce'))                           return 'AGENTFORCE';
  if (n.includes('servicenow'))                           return 'SERVICENOW';
  if (n.includes('uipath'))                               return 'UIPATH';
  if (n.includes('langchain'))                            return 'LANGCHAIN';
  if (n.includes('crewai'))                               return 'CREWAI';
  if (n.includes('autogen'))                              return 'AUTOGEN';
  return null;
}

const DEFAULT_FORM = {
  // platformType — the framework TYPE (e.g. 'ANTHROPIC'). This is the
  // first decision the operator makes; the connector instance is derived
  // (auto-selected if there's exactly one, prompted if multiple).
  platformType: '',
  // frameworkChoiceId — the tabbed picker's selected row id (e.g. 'CA_CLAUDE_CODE').
  // Maps to platformType; kept so the picker reflects the exact agent/mode chosen.
  frameworkChoiceId: '',
  // platformConnectorId — the specific connector instance the ExteLet
  // binds to. Auto-populated when only one connector of the chosen
  // platformType exists; explicitly chosen via the secondary picker when
  // multiple are configured.
  platformConnectorId: '',
  name: '',
  externalAgentId: '',
  externalAgentName: '',
  externalAgentVersion: '',
  invocationPattern: 'SYNCHRONOUS',
  maxTokensPerRequest: 8192,
  timeoutSeconds: 300,
  systemPromptOverride: '',
  integrationPath: 'CODED',
  /* Same default and shape the native Let create surface uses: creator owns what
   * they build, accepted inline → OwnershipRecord ACTIVE on create. Switching to
   * "nominate" leaves it NOMINATED until the nominee accepts. */
  accountability: DEFAULT_ACCOUNTABILITY,
};

/* Map Discovery's discoverySource to the auto-recommended integration path.
 * CODE_REPO → CODED (we found agent source in a repo, so they have code).
 * Anything else (NETWORK_TRAFFIC, CLOUD_INVENTORY, IDP, MARKETPLACE) →
 * MCP (we observed traffic but the operator likely can't modify the agent). */
function recommendedPath(seed) {
  if (!seed) return null;
  const src = String(seed.discoverySource || '').toUpperCase();
  if (src === 'CODE_REPO' || src === 'CODE_REPOSITORY') return 'CODED';
  if (src) return 'MCP';
  return null;
}

/* Stable ref so useWizardDraft's memoized callbacks don't churn each render.
 * `name` gives the central Drafts list a readable label from this flow's own field. */
const EXTELET_DRAFT_SERVER = {
  archetype: 'extelet',
  name: (snap) => snap?.form?.externalAgentName || snap?.form?.name || null,
};

export function ExteletRegistrationWizard({
  isOpen,
  onClose,
  seedFromDiscovery,           /* optional — pre-fills + auto-recommends */
  seedFromDraft,               /* optional — resume a server draft {id, form:<snapshot>} */
  onComplete,                  /* called with envelope after step 6 */
}) {
  const { addToast } = useToast();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [connectors, setConnectors] = useState(null);
  const [connectorsLoading, setConnectorsLoading] = useState(false);
  /* DEF-EXTELET-V3-CROSSSTORE-001 — generic-store connectors (Build > Connectors).
   * Probed alongside GEL platform connectors so we can give the operator an
   * honest "you have one over here, but ExteLet wants a separate Platform
   * Connector" message instead of the misleading "no connector configured". */
  const [genericConnectors, setGenericConnectors] = useState([]);
  const [envelope, setEnvelope] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [credsSaved, setCredsSaved] = useState(false);
  const [snippetLang, setSnippetLang] = useState('java');   /* Java default for the demo */
  const [copied, setCopied] = useState({});                 /* per-credential copy feedback */

  const autoRec = useMemo(() => recommendedPath(seedFromDiscovery), [seedFromDiscovery]);

  /* Draft persistence — survive close / navigate-away / mid-way failure so the
   * operator can resume instead of restarting from step 1. The ExteLet form
   * carries no secrets (credentials live on the Platform Connector), so nothing
   * is excluded; the hook still strips anything passed in excludeFields. */
  const { save: saveDraft, load: loadDraft, clear: clearDraft, adoptServerDraft } =
    useWizardDraft('prove7:extelet-wizard-draft', { server: EXTELET_DRAFT_SERVER });
  const [draftRestored, setDraftRestored] = useState(false);

  /* On open: restore a saved draft (if any and not Discovery-seeded), else seed
   * the form from Discovery / defaults. Then fetch the connector list so step 1
   * has the dropdown options. */
  useEffect(() => {
    if (!isOpen) return;
    setEnvelope(null);
    setSubmitError(null);
    setCredsSaved(false);

    const seeded = {
      ...DEFAULT_FORM,
      name:                 seedFromDiscovery?.name || seedFromDiscovery?.externalAgentName || '',
      externalAgentId:      seedFromDiscovery?.externalAgentId || seedFromDiscovery?.id || '',
      externalAgentName:    seedFromDiscovery?.externalAgentName || seedFromDiscovery?.name || '',
      externalAgentVersion: seedFromDiscovery?.version || seedFromDiscovery?.externalAgentVersion || '',
      platformType:         (seedFromDiscovery?.platformType || '').toUpperCase(),
      platformConnectorId:  seedFromDiscovery?.platformConnectorId || seedFromDiscovery?.suggestedConnectorId || '',
      integrationPath:      autoRec || 'CODED',
    };
    /* Priority: an explicit resume from the central Drafts panel (server draft) wins,
     * then a local draft, then a Discovery seed, then blank. Discovery-promote still
     * wins over a stale generic LOCAL draft. The server draft's `form` column holds the
     * whole snapshot {form, step}, so a panel snapshot has a nested `.form`. */
    const panelSnapshot = (seedFromDraft && seedFromDraft.form && seedFromDraft.form.form)
      ? seedFromDraft.form : null;
    if (panelSnapshot && seedFromDraft.id) adoptServerDraft(seedFromDraft.id);
    const saved = panelSnapshot ? panelSnapshot : (seedFromDiscovery ? null : loadDraft());
    if (saved && saved.form) {
      setForm({ ...DEFAULT_FORM, ...saved.form });
      /* Never resume into a post-mint step — drafts are only saved pre-mint. */
      setStep(Math.min(4, saved.step || 1));
      setDraftRestored(true);
    } else {
      setForm(seeded);
      setStep(1);
      setDraftRestored(false);
    }

    setConnectorsLoading(true);
    (async () => {
      try {
        /* Probe BOTH connector stores in parallel.
         *
         *   gelListConnectors  → the canonical source of truth for ExteLet
         *                        runtime bindings (the form's actual picker
         *                        list).
         *   getConnectors      → the generic connector store. We don't bind
         *                        ExteLets to these, but if the operator has
         *                        one configured for the same framework, we
         *                        need to tell them WHY their existing setup
         *                        doesn't carry over.
         *
         * The generic fetch failing isn't fatal — we just lose the smart
         * cross-store hint, the rest of the wizard still works. */
        const [gelRes, genericRes] = await Promise.all([
          api.gelListConnectors ? api.gelListConnectors() : Promise.resolve(null),
          api.getConnectors      ? api.getConnectors({}).catch(() => null) : Promise.resolve(null),
        ]);

        /* api.gelListConnectors unwraps the sharing envelope and always returns an
         * array, so connector fields are read directly here. */
        const flattened = Array.isArray(gelRes?.data) ? gelRes.data : [];
        /* Status is stored lowercase; compare case-insensitively so a backend casing
         * change cannot silently hide every connector. */
        const active = flattened.filter(
          c => !c.status || String(c.status).toUpperCase() === 'ACTIVE'
        );
        setConnectors(active);

        // api.getConnectors unwraps the sharing envelope and always returns an array.
        const genericList = Array.isArray(genericRes?.data) ? genericRes.data : [];
        const genericActive = genericList
          .filter(c => !c.status
            || c.status === 'ACTIVE'
            || c.status === 'connected'
            || c.status === 'CONNECTED');
        setGenericConnectors(genericActive);

        /* If only one ACTIVE connector matches the seed framework, pre-select it. */
        if (!seedFromDiscovery?.platformConnectorId && seedFromDiscovery?.platformType) {
          const match = active.find(c => (c.platformType || '').toUpperCase() === String(seedFromDiscovery.platformType).toUpperCase());
          if (match) setForm(prev => ({ ...prev, platformConnectorId: match.id }));
        }
      } catch (err) {
        addToast({ tone: 'danger', title: 'Could not load Agent Frameworks',
          description: err?.message || String(err) });
        setConnectors([]);
        setGenericConnectors([]);
      } finally {
        setConnectorsLoading(false);
      }
    })();
  }, [isOpen, seedFromDiscovery, seedFromDraft, autoRec, addToast, loadDraft, adoptServerDraft]);

  /* Auto-persist the in-progress draft on every form/step change, but only
   * pre-mint (step < 5) and before an envelope exists — once credentials are
   * minted the registration is real and there's nothing left to draft.
   * The Accountability step (4) is pre-mint, so the operator's owner choice is
   * carried in the draft like every other answer and survives a resume. */
  useEffect(() => {
    if (!isOpen) return;
    if (step >= 5 || envelope) return;
    saveDraft({ form, step });
  }, [isOpen, form, step, envelope, saveDraft]);

  /* Discard the draft and reset to a clean form (Discovery seed still honored). */
  const startFresh = () => {
    clearDraft();
    setForm({ ...DEFAULT_FORM, integrationPath: autoRec || 'CODED' });
    setStep(1);
    setSubmitError(null);
    setDraftRestored(false);
  };

  /* Explicit "Save & continue later" — the draft is already auto-saved; this
   * gives the operator a deliberate exit that confirms their progress is kept. */
  const saveAndClose = () => {
    saveDraft({ form, step });
    addToast({ tone: 'info', title: 'Draft saved',
      description: 'Resume anytime from + New Agentlet → Register external agent.' });
    onClose?.();
  };

  const set = (patch) => setForm(prev => ({ ...prev, ...patch }));

  const canContinue = () => {
    // Step 1 requires BOTH a framework type AND a resolved connector.
    // For a type with exactly one configured connector, the connector
    // auto-selects (handleTypeChange in Step1Framework), so the user
    // doesn't see a second picker. For 0-configured types, the operator
    // is blocked here and directed to the gel-connectors page.
    if (step === 1) return !!form.platformType && !!form.platformConnectorId;
    // External Agent ID is a proxied (vendor) concept; coded-SDK agents have none — the backend
    // auto-sets it to the minted Agent UUID. So it's only required for proxied frameworks.
    if (step === 2) return !!form.name && (isCodedFramework(form.platformType) || !!form.externalAgentId);
    if (step === 3) return form.integrationPath === 'CODED' || form.integrationPath === 'MCP';
    /* Accountability. "self" is complete on its own (the creator is the owner and
     * the accept checkbox defaults on). "nominate" is NOT complete until a person
     * is actually named — sending mode=nominate with a null principal would create
     * a nomination addressed to nobody, which no ceremony can ever resolve. */
    if (step === REGISTER_COMMIT_STEP) {
      const a = form.accountability || DEFAULT_ACCOUNTABILITY;
      if (a.ownerMode === 'nominate') return !!a.ownerPrincipalId;
      return true;
    }
    if (step === 5) return !!envelope && credsSaved;     /* checkbox + minted */
    if (step === 6) return true;
    return true;
  };

  const submitOnboard = async () => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      const payload = {
        name: form.name,
        platformConnectorId: form.platformConnectorId,
        externalAgentId: form.externalAgentId,
        externalAgentName: form.externalAgentName || form.name,
        externalAgentVersion: form.externalAgentVersion || '1.0.0',
        invocationPattern: form.invocationPattern,
        maxTokensPerRequest: Number(form.maxTokensPerRequest) || 8192,
        timeoutSeconds: Number(form.timeoutSeconds) || 300,
        systemPromptOverride: form.systemPromptOverride || null,
        integrationPath: form.integrationPath,
        /* Threaded to ExternalAgentRegistrationService → AgentService.createAgent
         * → OwnershipRepresentationService.establishOnCreate — the same shared
         * choke-point the native Let create posts to. */
        accountability: form.accountability || DEFAULT_ACCOUNTABILITY,
      };
      const res = await api.gelOnboardExteLet(payload);
      if (res && res.ok === false) {
        const msg = res.error || res.data?.error || `HTTP ${res.status || '?'}`;
        setSubmitError(msg);
        addToast({ tone: 'danger', title: 'Onboarding failed', description: msg });
        return false;
      }
      const env = res?.data || res;
      setEnvelope(env);
      /* Registration committed — discard the saved draft so a future open
       * starts clean instead of resurrecting this completed run. */
      clearDraft();
      setDraftRestored(false);
      addToast({ tone: 'success', title: 'ExteLet registered',
        description: `${form.name} is on file as PENDING_VERIFICATION — auto-verifies on first call.` });
      return true;
    } catch (err) {
      const msg = err?.message || String(err);
      setSubmitError(msg);
      addToast({ tone: 'danger', title: 'Onboarding failed', description: msg });
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  const goNext = async () => {
    /* Step 4 → 5 is where we actually submit. The user clicks "Mint
     * credentials" on the Accountability step and the wizard advances only
     * after the BE round-trip succeeds. */
    if (step === REGISTER_COMMIT_STEP) {
      const ok = await submitOnboard();
      if (ok) setStep(5);
      return;
    }
    if (step === 7) {
      onComplete?.(envelope);
      onClose?.();
      return;
    }
    setStep(s => Math.min(7, s + 1));
  };

  const goBack = () => {
    /* Step 5+ can't go back — credentials are already minted in the DB.
     * Operator must use the Registration tab from here. */
    if (step <= REGISTER_COMMIT_STEP) setStep(s => Math.max(1, s - 1));
  };

  const copyTo = (key, value) => {
    try {
      navigator.clipboard?.writeText(value);
      setCopied(prev => ({ ...prev, [key]: true }));
      setTimeout(() => setCopied(prev => ({ ...prev, [key]: false })), 1500);
    } catch (e) {
      addToast({ tone: 'warning', title: 'Clipboard unavailable',
        description: 'Copy manually from the field.' });
    }
  };

  const footer = (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, width: '100%' }}>
      <Button variant="ghost" onClick={goBack} disabled={step === 1 || step >= 5} iconLeft={<ArrowLeft />}>Back</Button>
      <div style={{ display: 'flex', gap: 8 }}>
        {step < 5 && (
          <Button variant="ghost" onClick={saveAndClose}>Save &amp; continue later</Button>
        )}
        <Button variant="secondary" onClick={onClose}>{step === 7 ? 'Close' : 'Cancel'}</Button>
        {step !== 7 && (
          <Button variant="primary" onClick={goNext}
                  disabled={!canContinue() || submitting}
                  loading={submitting && step === REGISTER_COMMIT_STEP}
                  iconRight={<ArrowRight />}>
            {step === REGISTER_COMMIT_STEP ? 'Mint credentials' : 'Continue'}
          </Button>
        )}
        {step === 7 && (
          <Button variant="primary" onClick={goNext} iconRight={<ArrowRight />}>
            Take me to the Registration tab
          </Button>
        )}
      </div>
    </div>
  );

  return (
    /* Per V2 design §5.5 + §3.13: detail/wizard surfaces are slideouts,
     * not centered modals. Centered modals interrupt the operator's
     * spatial context (where they were on the inventory page); slideouts
     * preserve it by sliding in from the right edge.
     *
     * Width 720px gives the 6-step Stepper room to breathe while the
     * underlying agentlets inventory stays visible to the left — the
     * operator can still see "the row I clicked from" while filling
     * out the wizard, which prevents the "where was I?" disorientation
     * the modal flavour introduced.
     *
     * Past step 4 (mint credentials) we disable backdrop + close — the
     * onboard transaction has committed and the plaintext keys are
     * shown ONCE; the operator must explicitly Activate. */
    <Slideout
      isOpen={isOpen}
      onClose={step >= 5 ? null /* irreversible past mint — force action */ : onClose}
      dismissOnBackdrop={step < 5}
      width={720}
      ariaLabel="Register ExteLet wizard"
      header={
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Sparkles size={16} aria-hidden="true" />
          <span style={{ fontWeight: 600 }}>Register ExteLet</span>
          {seedFromDiscovery && (
            <Pill tone="info" size="sm" style={{ marginLeft: 6 }}>via Discovery</Pill>
          )}
        </div>
      }
      footer={footer}
    >
      <Stepper step={step} />

      {draftRestored && step < 5 && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
          padding: '8px 12px', marginBottom: 14, borderRadius: 8,
          background: 'var(--v2-info-soft, rgba(0,90,180,0.06))',
          border: '0.5px solid var(--v2-border, rgba(0,0,0,0.08))',
          fontSize: 12.5,
        }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <Info size={14} aria-hidden="true" />
            Draft restored from a previous session.
          </span>
          <button type="button" onClick={startFresh}
            style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer',
              color: 'var(--v2-action-strong, #0e3552)', fontWeight: 500, fontSize: 12.5 }}>
            Start fresh
          </button>
        </div>
      )}

      {step === 1 && <Step1Framework form={form} set={set} connectors={connectors} genericConnectors={genericConnectors} loading={connectorsLoading} seed={seedFromDiscovery} />}
      {step === 2 && <Step2Connect form={form} set={set} seed={seedFromDiscovery} />}
      {step === 3 && <Step3Path form={form} set={set} autoRec={autoRec} submitError={submitError} />}
      {step === 4 && <StepAccountability form={form} setForm={setForm} />}
      {step === 5 && <Step4Credentials envelope={envelope} credsSaved={credsSaved} setCredsSaved={setCredsSaved} copyTo={copyTo} copied={copied} />}
      {step === 6 && <Step5Wire envelope={envelope} lang={snippetLang} setLang={setSnippetLang} copyTo={copyTo} copied={copied} integrationPath={form.integrationPath} />}
      {step === 7 && <Step6Activate envelope={envelope} agentName={form.name} integrationPath={form.integrationPath} />}
    </Slideout>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
 * Stepper indicator — clickable dots for steps 1-3 (mutable), read-only
 * checkmarks for steps 4-6 (post-mint).
 * ─────────────────────────────────────────────────────────────────────── */
function Stepper({ step }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 4,
      padding: '4px 0 18px', borderBottom: '0.5px solid var(--v2-border, rgba(0,0,0,0.08))',
      marginBottom: 18, flexWrap: 'wrap',
    }}>
      {STEPS.map((s, i) => {
        const done = step > s.n;
        const active = step === s.n;
        const Icon = s.icon;
        return (
          <React.Fragment key={s.n}>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '4px 8px', borderRadius: 6,
              background: active ? 'var(--v2-text-primary)' : (done ? 'var(--v2-success-soft)' : 'transparent'),
              color: active ? 'var(--v2-bg-surface)' : (done ? 'var(--v2-success)' : 'var(--v2-ink-muted, var(--v2-text-muted))'),
              fontSize: 11.5, fontWeight: active ? 500 : 400,
            }}>
              <span style={{
                width: 18, height: 18, borderRadius: '50%',
                background: active ? 'rgba(255,255,255,0.2)' : (done ? 'var(--v2-success)' : 'rgba(0,0,0,0.05)'),
                color: active ? 'var(--v2-bg-surface)' : (done ? 'var(--v2-bg-surface)' : 'inherit'),
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 10.5, fontWeight: 600,
              }}>{/* The step's own icon. It was declared on every step and then
                     drawn over with a round number, so none of them had ever
                     been seen. The number is still the fallback for a step that
                     carries no icon. */}
                {Icon ? <Icon size={11} strokeWidth={2.25} aria-hidden="true" /> : (done ? '✓' : s.n)}
              </span>
              <span>{s.label}</span>
            </div>
            {i < STEPS.length - 1 && (
              <span style={{ width: 14, height: 1, background: 'rgba(0,0,0,0.1)' }} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
 * Step 1 · Bind Agent Framework
 * ─────────────────────────────────────────────────────────────────────── */
function Step1Framework({ form, set, connectors, genericConnectors, loading, seed }) {
  // Connectors of the currently-selected framework type. Drives the
  // secondary connector picker (only renders when multiple) and the
  // "no connector yet" hint when the type has none.
  const typeConnectors = (connectors || []).filter(
    (c) => (c.platformType || '').toUpperCase() === form.platformType
  );
  const hasMultipleConnectors = typeConnectors.length > 1;
  const hasNoConnector = !!form.platformType && typeConnectors.length === 0;

  /* DEF-EXTELET-V3-CROSSSTORE-001 — when no Platform Connector is configured
   * for the chosen framework but the operator HAS a matching connector in the
   * generic store, surface that explicitly. */
  const genericMatches = useMemo(() => {
    if (!form.platformType) return [];
    return (genericConnectors || []).filter(
      (c) => mapGenericConnectorToFramework(c) === form.platformType
    );
  }, [genericConnectors, form.platformType]);

  // When the user picks a framework type:
  //   - 1 connector of that type configured → auto-select it (no extra click)
  //   - 0 or >1 → clear platformConnectorId; let user pick or create
  const handleTypeChange = (nextType) => {
    const matches = (connectors || []).filter(
      (c) => (c.platformType || '').toUpperCase() === nextType
    );
    set({
      platformType: nextType,
      platformConnectorId: matches.length === 1 ? matches[0].id : '',
    });
  };

  return (
    <div>
      <h3 style={{ fontSize: 16, fontWeight: 500, margin: '0 0 4px' }}>Bind to an Agent Framework</h3>
      <p style={{ fontSize: 13, color: 'var(--v2-text-secondary)', margin: '0 0 16px', lineHeight: 1.55 }}>
        Pick the framework this ExteLet runs on. The same taxonomy that powers
        the <RouterLink to="/gel-connectors"
              style={{ color: 'var(--v2-info, var(--v2-accent-cyan))' }}>External Agent Framework</RouterLink> page.
        Auth, health probes, and routing are handled by the platform connector you (or another
        admin) configured for this framework.
      </p>

      {seed?.platformType && (
        <div style={{
          background: 'var(--v2-success-soft)', color: 'var(--v2-success)', padding: '9px 12px',
          borderRadius: 6, fontSize: 12.5, marginBottom: 12,
          display: 'flex', gap: 8, alignItems: 'flex-start', lineHeight: 1.5,
        }}>
          <Sparkles size={14} aria-hidden="true" style={{ flex: '0 0 14px', marginTop: 1 }} />
          <span>Discovery saw a <strong style={{ fontWeight: 500 }}>{seed.platformType}</strong> signature
            ({seed.discoverySource || 'scan'}) — pre-selected below.</span>
        </div>
      )}

      {/* Primary picker — FRAMEWORK TYPE. The same canonical list shown on
       * /gel-connectors's "Add External Agent Framework" wizard. */}
      <label style={{ display: 'block', marginBottom: 12 }}>
        <span style={{ fontSize: 11, fontWeight: 500, textTransform: 'uppercase',
                       letterSpacing: '0.04em', color: 'var(--v2-ink-muted, var(--v2-text-muted))',
                       display: 'block', marginBottom: 4 }}>
          Framework
        </span>
        <EntityPicker
          ariaLabel="Agent framework"
          items={FRAMEWORK_CHOICES}
          value={form.frameworkChoiceId || firstChoiceForType(form.platformType)?.id || ''}
          groups={FRAMEWORK_TABS}
          getGroupId={(x) => x.group}
          getId={(x) => x.id}
          getLabel={(x) => x.label}
          getSublabel={(x) => {
            const n = (connectors || []).filter(
              (c) => (c.platformType || '').toUpperCase() === x.platformType).length;
            return `${x.desc} · ${n ? `${n} connector${n === 1 ? '' : 's'} configured` : 'no connector configured'}`;
          }}
          getSortKey={() => ''}
          disabled={loading}
          placeholder="Select an agent framework"
          searchPlaceholder="Search frameworks & agents"
          onChange={(id) => {
            const c = choiceById(id);
            if (!c) return;
            set({ frameworkChoiceId: id });
            handleTypeChange(c.platformType);
          }}
        />
      </label>

      {/* Secondary picker — only surfaces when the chosen type has >1
       * configured connector. For 0 or 1, this stays collapsed and either
       * the auto-select (single) or the no-connector hint (zero) handles
       * the disambiguation. */}
      {hasMultipleConnectors && (
        <label style={{ display: 'block', marginBottom: 12 }}>
          <span style={{ fontSize: 11, fontWeight: 500, textTransform: 'uppercase',
                         letterSpacing: '0.04em', color: 'var(--v2-ink-muted, var(--v2-text-muted))',
                         display: 'block', marginBottom: 4 }}>
            Configured connector
          </span>
          <select
            value={form.platformConnectorId}
            onChange={(e) => set({ platformConnectorId: e.target.value })}
            style={{
              width: '100%', padding: '8px 10px', fontSize: 13,
              border: '0.5px solid rgba(0,0,0,0.15)', borderRadius: 6,
              background: 'var(--v2-bg-surface)', color: 'var(--v2-text-primary)',
            }}
          >
            <option value="">— Pick a connector —</option>
            {typeConnectors.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}{c.healthStatus ? ` · ${c.healthStatus}` : ''}
              </option>
            ))}
          </select>
          <span style={{ fontSize: 11, color: 'var(--v2-ink-tertiary, var(--v2-text-muted))', display: 'block', marginTop: 4 }}>
            {typeConnectors.length} connectors configured for this framework.
            Pick the one this ExteLet should route through.
          </span>
        </label>
      )}

      {/* Zero-configured hint. Two flavors:
        *
        *   1. Operator has NO connector anywhere → "Configure one on EAF page".
        *   2. Operator has a matching connector in the GENERIC store but no
        *      Platform Connector → explain the distinction and link them to
        *      the EAF setup page (which is the one wired for ExteLet
        *      runtime bindings). DEF-EXTELET-V3-CROSSSTORE-001. */}
      {hasNoConnector && genericMatches.length > 0 && (
        <div style={{
          background: 'var(--v2-accent-cyan-soft)', color: 'var(--v2-accent-cyan)', padding: '10px 12px',
          borderRadius: 6, fontSize: 12.5, marginBottom: 12,
          lineHeight: 1.5, border: '0.5px solid var(--v2-accent-cyan-soft)',
        }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
            <Info size={14} aria-hidden="true" style={{ flex: '0 0 14px', marginTop: 1 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 500, marginBottom: 4 }}>
                {genericMatches.length === 1
                  ? <>You already have <strong>{genericMatches[0].name}</strong> in Build &rsaquo; Connectors</>
                  : <>You have {genericMatches.length} matching connector{genericMatches.length === 1 ? '' : 's'} in Build &rsaquo; Connectors</>
                }
              </div>
              <div style={{ marginBottom: 6 }}>
                That configures <strong>tool calls FROM</strong> a Prove7-governed agent
                to {typeLabel(form.platformType)}.
                {' '}ExteLet runtime binding is the inverse — it routes an{' '}
                <strong>externally-hosted agent THROUGH</strong> the platform — and
                needs its own Platform Connector for health probes, identity, and routing.
              </div>
              <RouterLink to="/gel-connectors"
                 style={{ color: 'var(--v2-accent-cyan)', fontWeight: 500, textDecoration: 'underline' }}>
                Configure {typeLabel(form.platformType)} Platform Connector →
              </RouterLink>{' '}
              <span style={{ color: 'var(--v2-accent-cyan)' }}>
                (same credentials, ~30 seconds)
              </span>
            </div>
          </div>
        </div>
      )}
      {hasNoConnector && genericMatches.length === 0 && (
        <div style={{
          background: 'var(--v2-warning-soft)', color: 'var(--v2-warning)', padding: '9px 12px',
          borderRadius: 6, fontSize: 12.5, marginBottom: 12,
          lineHeight: 1.5,
        }}>
          No connector configured for <strong>{typeLabel(form.platformType)}</strong> yet.{' '}
          <RouterLink to="/gel-connectors"
             style={{ color: 'var(--v2-warning)', fontWeight: 500 }}>
            Add one on the External Agent Frameworks page
          </RouterLink>{' '}— it'll appear here on your next visit.
        </div>
      )}

      <p style={{ fontSize: 11.5, color: 'var(--v2-ink-tertiary, var(--v2-text-muted))', marginTop: 8 }}>
        Don't see a framework you need? Open the{' '}
        <RouterLink to="/gel-connectors"
           style={{ color: 'var(--v2-info, var(--v2-accent-cyan))' }}>External Agent Framework page</RouterLink>
        {' '}to add a new connector.
      </p>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
 * Step 2 · Connect the ExteLet  (renamed per user feedback from "Identify")
 * ─────────────────────────────────────────────────────────────────────── */
function Step2Connect({ form, set, seed }) {
  return (
    <div>
      <h3 style={{ fontSize: 16, fontWeight: 500, margin: '0 0 4px' }}>Connect the ExteLet</h3>
      <p style={{ fontSize: 13, color: 'var(--v2-text-secondary)', margin: '0 0 16px', lineHeight: 1.55 }}>
        {seed
          ? 'We pre-filled everything we learned during the scan. Confirm or edit.'
          : 'Tell us how to identify this ExteLet. Defaults are tuned for synchronous LLM agents.'}
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Field label="Display name" required>
          <input value={form.name} onChange={(e) => set({ name: e.target.value })}
                 placeholder="partner-llm-7" style={inputStyle} />
        </Field>
        <Field label="Version">
          <input value={form.externalAgentVersion}
                 onChange={(e) => set({ externalAgentVersion: e.target.value })}
                 placeholder="1.0.0" style={inputStyle} />
        </Field>
      </div>

      {/* External Agent ID + Invocation pattern are PROXIED concepts — the vendor agent id the
        * gateway dispatches to, and how it dispatches. A coded-SDK agent runs in your process and
        * calls the gateway, so neither applies: the id is auto-set to the minted Agent UUID. */}
      {isCodedFramework(form.platformType) ? (
        <div style={{ fontSize: 12, color: 'var(--v2-text-secondary)', lineHeight: 1.55,
                      padding: '10px 12px', border: '1px solid var(--v2-border-subtle)',
                      borderRadius: 8, background: 'var(--v2-surface-subtle)' }}>
          <strong style={{ color: 'var(--v2-text)' }}>Coded-SDK agent.</strong> No vendor agent id or
          invocation pattern — this agent is protected in-process via the SDK and calls the gateway.
          Its identifier is the <strong>Agent UUID</strong> minted on the next step.
        </div>
      ) : (
        <>
          <Field label="External agent ID" required>
            <input value={form.externalAgentId} onChange={(e) => set({ externalAgentId: e.target.value })}
                   placeholder="partner-llm-7 or claude-3-5-sonnet-20241022" style={inputStyle} />
          </Field>

          <Field label="Invocation pattern">
            <select value={form.invocationPattern} onChange={(e) => set({ invocationPattern: e.target.value })}
                    style={inputStyle}>
              <option value="SYNCHRONOUS">SYNCHRONOUS — request / response (recommended for LLM calls)</option>
              <option value="ASYNCHRONOUS">ASYNCHRONOUS — job submit + poll</option>
              <option value="STREAMING">STREAMING — server-sent events</option>
              <option value="WEBHOOK">WEBHOOK — fire-and-forget</option>
            </select>
          </Field>
        </>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Field label="Max tokens / request">
          <input type="number" value={form.maxTokensPerRequest}
                 onChange={(e) => set({ maxTokensPerRequest: e.target.value })}
                 style={inputStyle} />
        </Field>
        <Field label="Timeout (seconds)">
          <input type="number" value={form.timeoutSeconds}
                 onChange={(e) => set({ timeoutSeconds: e.target.value })}
                 style={inputStyle} />
        </Field>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
 * Step 3 · Choose integration path
 * ─────────────────────────────────────────────────────────────────────── */
function Step3Path({ form, set, autoRec, submitError }) {
  const PathCard = ({ value, title, icon: Icon, description, recommended }) => {
    const selected = form.integrationPath === value;
    return (
      <button
        type="button"
        onClick={() => set({ integrationPath: value })}
        style={{
          textAlign: 'left', width: '100%', cursor: 'pointer',
          border: selected ? '1.5px solid var(--v2-accent-cyan)' : '0.5px solid rgba(0,0,0,0.15)',
          borderRadius: 8, padding: '12px 14px',
          background: selected ? 'var(--v2-accent-cyan-soft)' : 'var(--v2-bg-surface)',
          marginBottom: 10, display: 'block',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
          <Icon size={16} aria-hidden="true" />
          <span style={{ fontWeight: 500, fontSize: 13 }}>{title}</span>
          {recommended && (
            <Pill tone="success" size="sm" style={{ marginLeft: 'auto' }}>
              Recommended
            </Pill>
          )}
        </div>
        <p style={{ fontSize: 12, color: 'var(--v2-text-secondary)', margin: 0, lineHeight: 1.55 }}>
          {description}
        </p>
      </button>
    );
  };

  return (
    <div>
      <h3 style={{ fontSize: 16, fontWeight: 500, margin: '0 0 4px' }}>How does this agent talk to its model?</h3>
      <p style={{ fontSize: 13, color: 'var(--v2-text-secondary)', margin: '0 0 16px', lineHeight: 1.55 }}>
        The integration path determines which credentials we mint and how invocations flow through protection.
      </p>

      <PathCard
        value="CODED"
        title="Coded agent — you own the source"
        icon={Code2}
        description="Embed the Agentlets SDK decorator (Python @governed or Java @Governed via Spring AOP). Every invocation is wrapped by governance with full request / response visibility. Best for langchain, langgraph, crewai, autogen agents and custom Java services."
        recommended={autoRec === 'CODED'}
      />
      <PathCard
        value="MCP"
        title="Off-the-shelf agent — route through MCP Gateway"
        icon={Plug}
        description="No code change to the agent itself. Configure the agent to point its model calls at the Agentlets MCP Gateway URL with the issued key. Best for ServiceNow Now Assist, UiPath, AgentForce, vendor SaaS agents."
        recommended={autoRec === 'MCP'}
      />

      {submitError && (
        <div style={{
          background: 'var(--v2-danger-soft)', color: 'var(--v2-danger)', padding: '9px 12px',
          borderRadius: 6, fontSize: 12.5, marginTop: 12,
          display: 'flex', gap: 8, alignItems: 'flex-start', lineHeight: 1.5,
        }}>
          <AlertTriangle size={14} aria-hidden="true" style={{ flex: '0 0 14px', marginTop: 1 }} />
          <span><strong style={{ fontWeight: 500 }}>Onboarding failed:</strong> {submitError}. Adjust above and click Mint credentials again — nothing has been persisted.</span>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
 * Step 4 · Mint credentials (shown after successful BE round-trip)
 * ─────────────────────────────────────────────────────────────────────── */
/**
 * Step 4 · Accountability — the LAST gate before credentials are minted.
 *
 * <p>This step closes a governance hole rather than adding a feature. The native
 * Let create surface (AgentCreateSlideout) has always asked who is accountable,
 * and the backend applies that answer at the single createAgent choke-point via
 * OwnershipRepresentationService.establishOnCreate. The ExteLet wizard never
 * asked, and the registration service persisted its Agent through
 * agentRepository.save() directly — so it bypassed that choke-point entirely and
 * every externally-registered agent entered the estate with NO ownership record.
 * The Execution-Ready gate that looks for one (OwnershipPromotionGate, wired into
 * ExternalAgentLifecycleService) therefore had nothing to find.</p>
 *
 * <p>The panel is the SHARED AccountabilitySection — the same component, the same
 * form.accountability shape, the same three outcomes — not a parallel
 * implementation. Placement is deliberate: minting writes the registration and
 * hands out a working credential, so accountability is answered before that,
 * never after.</p>
 */
function StepAccountability({ form, setForm }) {
  return (
    <div>
      <h3 style={{ fontSize: 16, fontWeight: 500, margin: '0 0 4px' }}>Who is accountable for this ExteLet?</h3>
      <p style={{ fontSize: 13, color: 'var(--v2-ink-muted)', margin: '0 0 16px', lineHeight: 1.55 }}>
        This agent runs on someone else&rsquo;s infrastructure, which makes the accountable
        human on our side more important, not less &mdash; they are the only party inside
        Prove7 answerable for what it does. The next step mints its credentials, so this
        is the last point before it can call anything.
      </p>
      <AccountabilitySection form={form} setForm={setForm} />
    </div>
  );
}

function Step4Credentials({ envelope, credsSaved, setCredsSaved, copyTo, copied }) {
  if (!envelope) {
    return (
      <div style={{ padding: '24px 0', textAlign: 'center', color: 'var(--v2-ink-muted, var(--v2-text-muted))', fontSize: 13 }}>
        Minting credentials…
      </div>
    );
  }

  const gw = envelope.gatewayKey;
  const sdk = envelope.sdkKey;
  const mcp = envelope.mcpKey;
  const agentUuid = envelope.snippets?.agentUuid || envelope.registration?.agentId;

  const CredRow = ({ keyName, cred, ckey, hint }) => (
    <div style={{
      background: 'rgba(0,0,0,0.03)', padding: '10px 12px', borderRadius: 6,
      marginTop: 8,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
        <span style={{ fontSize: 11, color: 'var(--v2-text-secondary)' }}>{keyName}</span>
        <button
          type="button"
          onClick={() => copyTo(ckey, cred.plaintext)}
          style={{
            background: 'none', border: 0, fontSize: 11.5,
            color: 'var(--v2-info, var(--v2-accent-cyan))', cursor: 'pointer',
            display: 'inline-flex', alignItems: 'center', gap: 4,
          }}
        >
          {copied[ckey] ? <Check size={12} /> : <Copy size={12} />}
          {copied[ckey] ? 'Copied' : 'Copy'}
        </button>
      </div>
      <code style={{
        fontFamily: 'var(--v2-font-mono, ui-monospace, monospace)',
        fontSize: 11.5, color: 'var(--v2-text-primary)',
        wordBreak: 'break-all', display: 'block',
      }}>{cred.plaintext}</code>
      {hint && <p style={{ fontSize: 10.5, color: 'var(--v2-ink-tertiary, var(--v2-text-muted))', margin: '4px 0 0' }}>{hint}</p>}
    </div>
  );

  return (
    <div>
      <h3 style={{ fontSize: 16, fontWeight: 500, margin: '0 0 4px' }}>Credentials minted — save these now</h3>
      <p style={{ fontSize: 13, color: 'var(--v2-text-secondary)', margin: '0 0 12px', lineHeight: 1.55 }}>
        All credentials are scoped to <code style={{ fontSize: 11.5 }}>this agent only</code>. They're shown <strong style={{ fontWeight: 500 }}>exactly once</strong> — we keep bcrypt hashes server-side.
      </p>

      {gw && <CredRow keyName="Gateway API key · scopes inbound traffic" cred={gw} ckey="gw" />}
      {sdk && <CredRow keyName="SDK key · authenticates the @Governed decorator" cred={sdk} ckey="sdk" />}
      {mcp && <CredRow keyName="MCP Gateway key · authenticates the MCP client" cred={mcp} ckey="mcp"
                       hint={mcp.plaintext?.includes('DEMO_PLACEHOLDER')
                         ? 'Demo placeholder · real MCP_GATEWAY key minting ships in V3.1.'
                         : null} />}

      <div style={{
        background: 'rgba(0,0,0,0.03)', padding: '10px 12px', borderRadius: 6,
        marginTop: 8,
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
          <span style={{ fontSize: 11, color: 'var(--v2-text-secondary)' }}>Agent UUID · stable identifier</span>
          <button type="button" onClick={() => copyTo('uuid', agentUuid)}
                  style={{ background: 'none', border: 0, fontSize: 11.5,
                           color: 'var(--v2-info, var(--v2-accent-cyan))', cursor: 'pointer',
                           display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            {copied.uuid ? <Check size={12} /> : <Copy size={12} />}
            {copied.uuid ? 'Copied' : 'Copy'}
          </button>
        </div>
        <code style={{ fontFamily: 'var(--v2-font-mono, ui-monospace, monospace)', fontSize: 11.5,
                       color: 'var(--v2-text-primary)', wordBreak: 'break-all', display: 'block' }}>
          {agentUuid}
        </code>
      </div>

      <div style={{
        background: 'var(--v2-warning-soft)', color: 'var(--v2-warning)', padding: '9px 12px',
        borderRadius: 6, fontSize: 12.5, marginTop: 14,
        display: 'flex', gap: 8, alignItems: 'flex-start', lineHeight: 1.5,
      }}>
        <AlertTriangle size={14} aria-hidden="true" style={{ flex: '0 0 14px', marginTop: 1 }} />
        <span><strong style={{ fontWeight: 500 }}>These keys won't be shown again.</strong> Store them in your secrets manager before continuing. You can rotate them later from the Registration tab.</span>
      </div>

      <label style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 12, fontSize: 12.5, cursor: 'pointer' }}>
        <input type="checkbox" checked={credsSaved} onChange={(e) => setCredsSaved(e.target.checked)} />
        I have securely saved these credentials.
      </label>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
 * Step 5 · Wire it up (Python | Java | MCP snippet tabs)
 * ─────────────────────────────────────────────────────────────────────── */
function Step5Wire({ envelope, lang, setLang, copyTo, copied, integrationPath }) {
  const snippets = envelope?.snippets || {};
  const isMcpPath = integrationPath === 'MCP';
  /* If MCP path was chosen, default to the MCP tab; otherwise honor the
   * lang state (Java is the default for the demo). */
  const effectiveLang = isMcpPath && !lang.startsWith('mcp') ? 'mcp' : lang;
  const code = effectiveLang === 'python' ? snippets.python
             : effectiveLang === 'java'   ? snippets.java
             :                              snippets.mcpConfig;

  const Tab = ({ value, label }) => (
    <button
      type="button"
      onClick={() => setLang(value)}
      style={{
        fontSize: 11.5, padding: '4px 11px', borderRadius: 4,
        cursor: 'pointer', border: 0,
        background: effectiveLang === value ? 'var(--v2-bg-surface)' : 'transparent',
        color: effectiveLang === value ? 'var(--v2-text-primary)' : 'var(--v2-text-secondary)',
        boxShadow: effectiveLang === value ? '0 0 0 0.5px rgba(0,0,0,0.15)' : 'none',
      }}
    >{label}</button>
  );

  return (
    <div>
      <h3 style={{ fontSize: 16, fontWeight: 500, margin: '0 0 4px' }}>Drop this into your agent code</h3>
      <p style={{ fontSize: 13, color: 'var(--v2-text-secondary)', margin: '0 0 12px', lineHeight: 1.55 }}>
        One decorator. Every invocation now flows through Agentlets protection — DPG, policy, audit chain, trust scoring.
      </p>

      <div style={{
        display: 'inline-flex', gap: 0, background: 'rgba(0,0,0,0.05)',
        padding: 2, borderRadius: 6, marginBottom: 10,
      }}>
        <Tab value="python" label="Python" />
        <Tab value="java" label="Java · Spring AOP" />
        <Tab value="mcp" label="MCP config" />
      </div>

      <div style={{
        background: '#0e1116', color: 'var(--v2-text-inverse-secondary)',
        fontFamily: 'var(--v2-font-mono, ui-monospace, monospace)', fontSize: 11.5,
        padding: '12px 14px', borderRadius: 6,
        whiteSpace: 'pre', overflowX: 'auto', position: 'relative', lineHeight: 1.55,
      }}>
        <button type="button" onClick={() => copyTo('snippet', code)}
                style={{
                  position: 'absolute', top: 8, right: 8,
                  background: '#1a1f29', color: 'var(--v2-text-inverse-secondary)',
                  border: '0.5px solid #2a3142', padding: '2px 8px',
                  fontSize: 10.5, borderRadius: 4, cursor: 'pointer',
                  fontFamily: 'var(--v2-font-sans, sans-serif)',
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                }}>
          {copied.snippet ? <Check size={11} /> : <Copy size={11} />}
          {copied.snippet ? 'Copied' : 'Copy'}
        </button>
        {code || '// (snippet unavailable)'}
      </div>

      <div style={{
        background: 'var(--v2-accent-cyan-soft)', color: 'var(--v2-accent-cyan)', padding: '9px 12px',
        borderRadius: 6, fontSize: 12, marginTop: 12,
        display: 'flex', gap: 8, alignItems: 'flex-start', lineHeight: 1.5,
      }}>
        <Info size={13} aria-hidden="true" style={{ flex: '0 0 13px', marginTop: 2 }} />
        <span>Re-show this snippet anytime from the Registration tab. Keys rotate there too — without re-issuing the registration.</span>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
 * Step 6 · Activate
 * ─────────────────────────────────────────────────────────────────────── */
function Step6Activate({ envelope, agentName, integrationPath }) {
  return (
    <div>
      <h3 style={{ fontSize: 16, fontWeight: 500, margin: '0 0 4px' }}>You're ready to deploy</h3>
      <p style={{ fontSize: 13, color: 'var(--v2-text-secondary)', margin: '0 0 14px', lineHeight: 1.55 }}>
        Registration is on file as <Pill tone="warning" size="sm" dot>PENDING_VERIFICATION</Pill>.
        The first successful protected call from your agent will auto-flip the registration to VERIFIED.
        No manual Verify step needed.
      </p>

      <Card padding="sm">
        <Card.Body>
          <dl style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: '6px 12px', fontSize: 12.5, margin: 0 }}>
            <dt style={{ color: 'var(--v2-ink-muted, var(--v2-text-muted))' }}>Agent</dt>
            <dd style={{ margin: 0 }}>{agentName} · <code style={{ fontSize: 11 }}>{envelope?.registration?.agentId}</code></dd>
            <dt style={{ color: 'var(--v2-ink-muted, var(--v2-text-muted))' }}>Path</dt>
            <dd style={{ margin: 0 }}>{integrationPath === 'CODED' ? 'Coded · Java / Python decorator' : 'Off-the-shelf · MCP Gateway'}</dd>
            <dt style={{ color: 'var(--v2-ink-muted, var(--v2-text-muted))' }}>Credentials</dt>
            <dd style={{ margin: 0 }}>{envelope?.gatewayKey ? '✓ Gateway' : ''}{envelope?.sdkKey ? ' · ✓ SDK' : ''}{envelope?.mcpKey ? ' · ✓ MCP' : ''}</dd>
            <dt style={{ color: 'var(--v2-ink-muted, var(--v2-text-muted))' }}>Auto-verify on</dt>
            <dd style={{ margin: 0 }}>First successful inbound call carrying the issued key</dd>
          </dl>
        </Card.Body>
      </Card>

      <div style={{
        background: 'var(--v2-success-soft)', color: 'var(--v2-success)', padding: '10px 12px',
        borderRadius: 6, fontSize: 12.5, marginTop: 14,
        display: 'flex', gap: 8, alignItems: 'flex-start', lineHeight: 1.55,
      }}>
        <ShieldCheck size={14} aria-hidden="true" style={{ flex: '0 0 14px', marginTop: 1 }} />
        <span><strong style={{ fontWeight: 500 }}>Audit trail is live.</strong> EXTERNAL_AGENT_ONBOARDED has already been emitted. EXTERNAL_AGENT_VERIFIED_FIRST_CALL will fire the moment your agent makes its first protected call.</span>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
 * Tiny shared bits
 * ─────────────────────────────────────────────────────────────────────── */
const inputStyle = {
  width: '100%', padding: '8px 10px', fontSize: 13,
  border: '0.5px solid rgba(0,0,0,0.15)', borderRadius: 6,
  background: 'var(--v2-bg-surface)', color: 'var(--v2-text-primary)',
};

function Field({ label, required, children }) {
  return (
    <label style={{ display: 'block', marginBottom: 10 }}>
      <span style={{
        fontSize: 11, fontWeight: 500, textTransform: 'uppercase',
        letterSpacing: '0.04em', color: 'var(--v2-ink-muted, var(--v2-text-muted))',
        display: 'block', marginBottom: 4,
      }}>
        {label}{required && <span style={{ color: 'var(--v2-danger)', marginLeft: 4 }}>*</span>}
      </span>
      {children}
    </label>
  );
}

export default ExteletRegistrationWizard;
